import { z } from 'zod';

export const API_VERSION = 'v1';
export const API_PREFIX = `/api/${API_VERSION}`;

/** Header carrying the per-session CSRF token on state-changing requests. */
export const CSRF_HEADER = 'x-csrf-token';

export const USERNAME_PATTERN = /^[A-Za-z0-9_]{3,20}$/;
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export const usernameSchema = z
  .string()
  .trim()
  .regex(USERNAME_PATTERN, 'Use 3 to 20 letters, numbers or underscores.');

/** Counted in code points, so accented letters and emoji are fair. */
export const newPasswordSchema = z
  .string()
  .refine((p) => Array.from(p).length >= PASSWORD_MIN_LENGTH, {
    message: `Use at least ${String(PASSWORD_MIN_LENGTH)} characters.`,
  })
  .refine((p) => Array.from(p).length <= PASSWORD_MAX_LENGTH, {
    message: `Use at most ${String(PASSWORD_MAX_LENGTH)} characters.`,
  });

/**
 * Optional accounts (ADR 0009): a guest adds a username and password to keep their record.
 * There is no email and no password reset.
 */
export const registerRequestSchema = z
  .object({ username: usernameSchema, password: newPasswordSchema })
  .strict()
  .refine((v) => !v.password.toLowerCase().includes(v.username.toLowerCase()), {
    path: ['password'],
    message: 'Your password must not contain your username.',
  });

export const signInRequestSchema = z
  .object({
    username: z.string().trim().min(1, 'Enter your username.').max(40),
    password: z
      .string()
      .min(1, 'Enter your password.')
      .max(PASSWORD_MAX_LENGTH * 4),
  })
  .strict();

/**
 * A pass. Every browser has one: an anonymous guest (ADR 0008), or a registered player when
 * `account` is set, with the record of matches played to the end (ADR 0009).
 */
export const sessionResponseSchema = z.object({
  guest: z.object({ id: z.uuid() }),
  account: z
    .object({
      username: z.string(),
      wins: z.number().int().nonnegative(),
      losses: z.number().int().nonnegative(),
    })
    .nullable(),
  session: z.object({
    deviceLabel: z.string(),
    startedAt: z.iso.datetime({ offset: true }),
    lastActiveAt: z.iso.datetime({ offset: true }),
  }),
  csrfToken: z.string(),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;
export type RegisterRequest = z.input<typeof registerRequestSchema>;
export type SignInRequest = z.input<typeof signInRequestSchema>;

/** Deleting an account needs the password and the typed word DELETE. */
export const deleteAccountRequestSchema = z
  .object({
    password: z
      .string()
      .min(1, 'Enter your password.')
      .max(PASSWORD_MAX_LENGTH * 4),
    confirm: z.literal('DELETE', 'Type DELETE to confirm.'),
  })
  .strict();
export type DeleteAccountRequest = z.input<typeof deleteAccountRequestSchema>;

/** "Forget this browser" asks for an explicit confirmation word, like a typed DELETE. */
export const forgetGuestRequestSchema = z.object({ confirm: z.literal('FORGET') }).strict();
export type ForgetGuestRequest = z.input<typeof forgetGuestRequestSchema>;

export const errorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string(),
    issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    retryAfterSeconds: z.number().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;
